import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCalendarCheck, FaChartLine, FaChild, FaCoins, FaClipboardCheck, FaClipboardList, FaExclamationTriangle, FaSchool, FaTools, FaUserShield } from "react-icons/fa";
import { FaBriefcase, FaPeopleArrows } from "react-icons/fa6";
import { AuthContext } from "../../AuthProvider";
import { LdsOverviewHero } from "../../components/lds/LdsOverview";
import Spinner from "../../components/Spinner";
import type { DashboardCounts, DashboardPersonalLink, DashboardSectionTile, DashboardUpcomingItem, GiteaSummary } from "./types/dashboard.types";
import { useDashboardPermissions } from "./hooks/useDashboardPermissions";
import { useDashboardCounts } from "./hooks/useDashboardCounts";
import { usePersonalUpcoming } from "./hooks/usePersonalUpcoming";
import { useGiteaDashboardStatus } from "./hooks/useGiteaDashboardStatus";
import { useNoctisDashboardStatus } from "./hooks/useNoctisDashboardStatus";
import { useMailchimpDashboardStatus } from "./hooks/useMailchimpDashboardStatus";
import { useDashboardFeedback } from "./hooks/useDashboardFeedback";
import { safeLocale } from "./utils/dashboardDate.utils";
import DashboardPersonalOverview from "./components/DashboardPersonalOverview";
import DashboardOpenActions from "./components/DashboardOpenActions";
import DashboardNavigationGrid from "./components/DashboardNavigationGrid";
import DashboardIntegrations from "./components/DashboardIntegrations";
import DashboardFeedbackModal from "./components/DashboardFeedbackModal";
import DashboardQuickSearch from "./components/DashboardQuickSearch";

interface KeycloakLike {
  idTokenParsed?: {
    given_name?: string;
    family_name?: string;
    name?: string;
    preferred_username?: string;
    auth_time?: number;
  };
}

interface AuthContextValue {
  keycloak?: KeycloakLike | null;
  authenticated?: boolean;
  userRoles?: string[];
}

const emptyCounts: DashboardCounts = { applications: 0, mutations: 0, waiting: 0 };
const emptyGiteaSummary: GiteaSummary = { configured: true, total: 0, counts: {} };

const dashboardStyles = `
  @keyframes dashboardFadeIn {
    from {
      opacity: 0;
      transform: translateY(14px) scale(0.99);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .dashboard-enter {
    animation: dashboardFadeIn 520ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .dashboard-enter {
      animation: none !important;
    }
  }
`;

const getDisplayName = (keycloak?: KeycloakLike | null): string =>
  [keycloak?.idTokenParsed?.given_name, keycloak?.idTokenParsed?.family_name].filter(Boolean).join(" ") ||
  keycloak?.idTokenParsed?.name ||
  keycloak?.idTokenParsed?.preferred_username ||
  "";

const getRoleLabel = (roles: string[] | undefined, fallback: string): string =>
  roles
    ?.filter((role) => !["offline_access", "uma_authorization", "default-roles-werfklas", "realm-management", "account"].includes(role))
    .join(", ") || fallback;

const countNoctisQueue = (byStatus: unknown): number => {
  if (Array.isArray(byStatus)) {
    const match = byStatus.find((item) => {
      const record = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
      return String(record.status || record.key || record.name || "").toUpperCase() === "PENDING";
    });
    const record = match && typeof match === "object" ? (match as Record<string, unknown>) : {};
    return Number(record.count ?? record.value ?? 0) || 0;
  }
  if (byStatus && typeof byStatus === "object") {
    const record = byStatus as Record<string, unknown>;
    return Number(record.PENDING ?? record.pending ?? 0) || 0;
  }
  return 0;
};

const DashboardPage = () => {
  const auth = useContext(AuthContext) as AuthContextValue;
  const { keycloak, authenticated = false, userRoles } = auth;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = safeLocale(i18n.language);
  const permissions = useDashboardPermissions();
  const [selectedPersonalUpcoming, setSelectedPersonalUpcoming] = useState<DashboardUpcomingItem | null>(null);

  const countsQuery = useDashboardCounts(authenticated, permissions);
  const upcomingQuery = usePersonalUpcoming(permissions, t);
  const giteaQuery = useGiteaDashboardStatus(permissions.canSeeGiteaIssues);
  const noctisQuery = useNoctisDashboardStatus(authenticated && permissions.canSeeNoctis);
  const mailchimpQuery = useMailchimpDashboardStatus(authenticated && permissions.canSeeMailchimp);

  const displayName = getDisplayName(keycloak);
  const requesterName = displayName;
  const feedback = useDashboardFeedback(requesterName, t);

  const counts = countsQuery.data ?? emptyCounts;
  const giteaSummary = giteaQuery.data ?? emptyGiteaSummary;
  const giteaOpenCount = useMemo(() => (giteaSummary.counts.new ?? 0) + (giteaSummary.counts.inProgress ?? 0) + (giteaSummary.counts.onHold ?? 0), [giteaSummary]);
  const noctisData = noctisQuery.data;
  const noctisQueueTotal = useMemo(() => countNoctisQueue(noctisData?.outbox?.byStatus ?? noctisData?.outbox?.by_status ?? null), [noctisData]);
  const openActionsTotal = (counts.applications || 0) + (counts.mutations || 0) + (counts.waiting || 0) + giteaOpenCount + (permissions.canSeeNoctis ? noctisQueueTotal : 0);
  const openActionsTileCount =
    (permissions.canManageApplications ? 1 : 0) +
    (permissions.canManageMutations ? 1 : 0) +
    (permissions.canViewWaitingList ? 1 : 0) +
    (permissions.canSeeGiteaIssues ? 1 : 0) +
    (permissions.canSeeNoctis ? 1 : 0);

  const todayLabel = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date());
  const lastLoginLabel = keycloak?.idTokenParsed?.auth_time
    ? new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(
        new Date(keycloak.idTokenParsed.auth_time * 1000),
      )
    : t("dashboard.unknown", { defaultValue: "Unknown" });
  const userRoleLabel = getRoleLabel(userRoles, t("dashboard.no_roles", { defaultValue: "No roles" }));

  const getPlacementDefaultPath = useCallback(() => {
    if (permissions.canViewPlacementOverview) return "/plaatsing";
    if (permissions.canSeeApplications) return "/school/applications";
    if (permissions.canViewWaitingList) return "/school/waiting-list";
    if (permissions.canSeeFuturePlanning) return "/plaatsing/toekomstige-planning";
    if (permissions.canSeeClassroomsOverview) return "/school/classrooms/overview";
    return "/plaatsing";
  }, [
    permissions.canSeeApplications,
    permissions.canSeeClassroomsOverview,
    permissions.canSeeFuturePlanning,
    permissions.canViewPlacementOverview,
    permissions.canViewWaitingList,
  ]);

  const getReportsDefaultPath = useCallback(() => {
    if (permissions.canViewReportsRoot) return "/reports";
    if (permissions.canViewPrivacyReports) return "/reports/privacy";
    if (permissions.canViewStudentReports) return "/reports/students";
    if (permissions.attendanceFeatureEnabled && permissions.canViewAttendanceReports) return "/reports/attendance";
    if (permissions.canViewOutingsReport) return "/reports/outings";
    return "/reports";
  }, [
    permissions.attendanceFeatureEnabled,
    permissions.canViewAttendanceReports,
    permissions.canViewOutingsReport,
    permissions.canViewPrivacyReports,
    permissions.canViewReportsRoot,
    permissions.canViewStudentReports,
  ]);

  const getAdminDefaultPath = useCallback(() => {
    if (permissions.canSeeAdminRoot) return "/admin";
    if (permissions.canSeeNotificationsAdmin) return "/admin/notifications";
    if (permissions.canManageBackups) return "/admin/backups";
    if (permissions.canViewAdminLogs) return "/admin/logs";
    return "/admin";
  }, [
    permissions.canManageBackups,
    permissions.canSeeAdminRoot,
    permissions.canSeeNotificationsAdmin,
    permissions.canViewAdminLogs,
  ]);

  const personalLinks = useMemo<DashboardPersonalLink[]>(
    () =>
      [
        {
          key: "classrooms",
          visible: permissions.canViewMyClassrooms,
          path: "/my/classrooms",
          icon: <FaChild />,
          label: t("dashboard.myEnvironment.classrooms", { defaultValue: "My classrooms" }),
        },
        {
          key: "parentMeetings",
          visible: permissions.canViewMyParentMeetings,
          path: "/my/parent-meetings",
          icon: <FaCalendarCheck />,
          label: t("dashboard.myEnvironment.parentMeetings", { defaultValue: "Parent meetings" }),
        },
        {
          key: "agendaActivities",
          visible: permissions.canViewMyAgendaActivitiesPage,
          path: "/my/outings",
          icon: <FaClipboardList />,
          label: t("dashboard.myEnvironment.agendaActivities", { defaultValue: "Agenda & activities" }),
        },
      ].filter((item) => item.visible),
    [permissions.canViewMyAgendaActivitiesPage, permissions.canViewMyClassrooms, permissions.canViewMyParentMeetings, t],
  );

  const sectionTiles = useMemo<DashboardSectionTile[]>(
    () =>
      [
        {
          key: "myEnvironment",
          visible: permissions.canSeeMyEnvironment,
          path: "/my",
          icon: <FaBriefcase className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.myEnvironment.title", { defaultValue: "My environment" }),
          description: t("dashboard.sections.myEnvironment.description", { defaultValue: "Your classrooms, parent meetings and agenda activities." }),
        },
        {
          key: "placement",
          visible: permissions.canSeePlacementSection,
          path: getPlacementDefaultPath(),
          icon: <FaPeopleArrows className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.placement.title", { defaultValue: "Placement" }),
          description: t("dashboard.sections.placement.description", { defaultValue: "Applications, interest list, classroom overview and future planning." }),
        },
        {
          key: "school",
          visible: permissions.canSeeSchoolSection,
          path: "/school",
          icon: <FaSchool className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.school.title", { defaultValue: "School" }),
          description: t("dashboard.sections.school.description", { defaultValue: "Teachers, classrooms, school years and school planning." }),
        },
        {
          key: "signals",
          visible: permissions.canSeeSignalsSection,
          path: "/signaleringen",
          icon: <FaExclamationTriangle className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.signals.title", { defaultValue: "Signals" }),
          description: t("dashboard.sections.signals.description", { defaultValue: "Attention points and signals." }),
        },
        {
          key: "reports",
          visible: permissions.canSeeReportsSection,
          path: getReportsDefaultPath(),
          icon: <FaChartLine className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.reports.title", { defaultValue: "Reports" }),
          description: t("dashboard.sections.reports.description", { defaultValue: "Insights, reports and overviews." }),
        },
        {
          key: "finance",
          visible: permissions.canSeeFinanceSection,
          path: "/finance",
          icon: <FaCoins className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.finance.title", { defaultValue: "Finance" }),
          description: t("dashboard.sections.finance.description", { defaultValue: "Parent contributions, payments and bank accounts." }),
        },
        {
          key: "tools",
          visible: permissions.canSeeToolsSection,
          path: "/tools",
          icon: <FaTools className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.tools.title", { defaultValue: "Tools" }),
          description: t("dashboard.sections.tools.description", { defaultValue: "Directory and useful tools." }),
        },
        {
          key: "administration",
          visible: permissions.canSeeAdministrationSection,
          path: "/administratie",
          icon: <FaClipboardCheck className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.administration.title", { defaultValue: "Administration" }),
          description: t("dashboard.sections.administration.description", { defaultValue: "Mutation requests and administrative actions." }),
        },
        {
          key: "management",
          visible: permissions.canSeeManagementSection,
          path: getAdminDefaultPath(),
          icon: <FaUserShield className="text-3xl text-indigo-600 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-indigo-300" />,
          title: t("dashboard.sections.management.title", { defaultValue: "Management" }),
          description: t("dashboard.sections.management.description", { defaultValue: "Users, roles, notifications and maintenance." }),
        },
      ].filter((tile) => tile.visible),
    [getAdminDefaultPath, getPlacementDefaultPath, getReportsDefaultPath, permissions, t],
  );

  if (keycloak && !authenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100 dark:bg-slate-900">
        <Spinner />
        <p className="mt-4 text-lg font-medium text-slate-600 dark:text-slate-300">
          {t("dashboard.authenticating", { defaultValue: "Authenticating..." })}
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{dashboardStyles}</style>
      <main className="dashboard-enter px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <LdsOverviewHero
            icon={FaChartLine}
            eyebrow={t("dashboard.eyebrow", { defaultValue: "Overview" })}
            title={t("dashboard.title", { defaultValue: "Dashboard" })}
            description={t("dashboard.description", { defaultValue: "Your overview of actions, planning and integrations." })}
            actions={
              <button
                type="button"
                onClick={() => feedback.setOpen(true)}
                className="inline-flex items-center justify-center rounded-full border border-indigo-200 bg-white px-4 py-2 text-sm font-bold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:text-indigo-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:transform-none dark:border-white/10 dark:bg-white/10 dark:text-indigo-100"
              >
                {t("dashboard.report_issue", { defaultValue: "Report an issue" })}
              </button>
            }
          />

          <DashboardQuickSearch />

          <DashboardPersonalOverview
            authenticated={authenticated}
            canSeeMyEnvironment={permissions.canSeeMyEnvironment}
            displayName={displayName}
            todayLabel={todayLabel}
            openActionsTotal={openActionsTotal}
            personalLinks={personalLinks}
            upcoming={upcomingQuery.data ?? []}
            upcomingLoading={upcomingQuery.loading}
            selectedUpcoming={selectedPersonalUpcoming}
            userRoleLabel={userRoleLabel}
            lastLoginLabel={lastLoginLabel}
            locale={locale}
            t={t}
            onSelectUpcoming={setSelectedPersonalUpcoming}
            onNavigate={navigate}
          />

        <DashboardOpenActions
          t={t}
          locale={locale}
          counts={counts}
          actionsLoading={countsQuery.loading}
          actionsError={countsQuery.error}
          permissions={permissions}
          openActionsTileCount={openActionsTileCount}
          openActionsTotal={openActionsTotal}
          gitea={{ loading: giteaQuery.loading || giteaQuery.refreshing, error: giteaQuery.error, summary: giteaSummary, openCount: giteaOpenCount }}
          noctis={{
            loading: noctisQuery.loading || noctisQuery.refreshing,
            error: noctisQuery.error,
            reachability: noctisData?.reachability ?? null,
            outbox: noctisData?.outbox ?? null,
            status: noctisData?.status ?? null,
            queueTotal: noctisQueueTotal,
          }}
          mailchimp={{
            loading: mailchimpQuery.loading || mailchimpQuery.refreshing,
            error: mailchimpQuery.error,
            status: mailchimpQuery.data?.status ?? null,
            neverSynced: mailchimpQuery.data?.neverSynced ?? false,
          }}
          onNavigate={navigate}
        />

        <DashboardNavigationGrid tiles={sectionTiles} onNavigate={navigate} />

        <DashboardIntegrations
          visible={permissions.canSeeIntegrationsSection}
          canSeeGiteaIssues={permissions.canSeeGiteaIssues}
          canSeeMailchimp={permissions.canSeeMailchimp}
          canSeeNoctis={permissions.canSeeNoctis}
          canSeeEBoekhouden={permissions.canSeeEBoekhouden}
          t={t}
          onNavigate={navigate}
        />

        {sectionTiles.length === 0 ? (
          <p className="mt-6 text-slate-500 dark:text-slate-300">
            {t("dashboard.no_sections", {
              defaultValue: "No sections available with your current permissions.",
            })}
          </p>
        ) : null}
        </div>
      </main>

      <DashboardFeedbackModal
        open={feedback.open}
        form={feedback.form}
        loading={feedback.loading}
        message={feedback.message}
        error={feedback.error}
        canSubmit={feedback.canSubmit}
        t={t}
        onClose={feedback.close}
        onFieldChange={feedback.updateField}
        onFilesChange={(files) => void feedback.addScreenshots(files)}
        onSubmit={() => void feedback.submit()}
      />
    </>
  );
};

export default DashboardPage;
